import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  InputAdornment,
  Typography,
  TextField,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SwipeRightAltIcon from "@mui/icons-material/SwipeRightAlt";
import { alpha } from "@mui/material/styles";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { coreGet } from "@/services/coreApi";
import useVesselOptions from "@/features/vessel/hooks/useVesselOptions.jsx";
import { useAccessContext } from "@/contexts/AccessContext";
import { nowMs, nowUtc } from "@/utils/appNow";
import {
  routeFormatLabel,
  vesselAcceptsImportFormat,
} from "@/config/routeExportFormats";
import { validateDecimal } from "@/features/voyagePlanner/voyageSettingsFields";
import CustomRadio from "@/components/ui/CustomRadio";
import VesselAutocomplete from "@/components/ui/VesselAutocomplete";
import WizardFooter from "@/features/voyagePlanner/wizard/WizardFooter.jsx";
import WarnChip from "@/components/ui/WarnChip";
import {
  PANEL_CARD_SX,
  CARD_TITLE_SX,
  ROW_LABEL_SX,
  ROW_DESCRIPTION_SX,
  PANEL_SCROLLBAR_SX,
  PLAN_FIELD_SX,
  PLAN_DATEPICKER_SLOTPROPS,
  PLAN_DATEPICKER_FIELD_SX,
  PLAN_SPEED_FIELD_SX,
  PLAN_FIELD_CAPTION_SX,
  PLAN_UNIT_ADORNMENT_SX,
} from "@/theme/panelStyles";
import { WW_BLUE, PANEL_TEXT_FAINT } from "@/theme/tokens";

const CARD_SX = { ...PANEL_CARD_SX, mb: 1.25 };

const BASESPEED_RULES = { minValue: Number.EPSILON, maxValue: 40 };

const RADIO_LABEL_SX = { fontSize: "12px", fontWeight: 600, color: WW_BLUE };

const AUTOCOMPLETE_LISTBOX_SLOTPROPS = {
  listbox: {
    sx: {
      "& .MuiAutocomplete-option": { fontSize: "13px" },
      "& .MuiAutocomplete-option[aria-selected='true']": {
        bgcolor: alpha(WW_BLUE, 0.12),
      },
      "& .MuiAutocomplete-option[aria-selected='true'].Mui-focused": {
        bgcolor: alpha(WW_BLUE, 0.18),
      },
    },
  },
};

const normalizeVesselName = (name) =>
  String(name ?? "").trim().toLowerCase().replace(/^m\/?v\.?\s+/, "");

// Single vessel → that one; else match the file's IMO, then its vessel name; else none.
export function preselectImportVessel(vessels, fileVesselName, fileVesselImo) {
  if (vessels.length === 1) return vessels[0];
  const imo = String(fileVesselImo ?? "").trim();
  const byImo = imo ? vessels.find((v) => String(v?.imo ?? "").trim() === imo) : null;
  if (byImo) return byImo;
  const wanted = normalizeVesselName(fileVesselName);
  if (!wanted) return null;
  return vessels.find((v) => normalizeVesselName(v?.name) === wanted) ?? null;
}

// File times are UTC; showing them in the browser's zone would shift them.
export const formatTimeUtc = (value) => {
  if (value == null || value === "") return "—";
  const ms = typeof value === "number" ? value : Date.parse(value);
  if (!Number.isFinite(ms)) return "—";
  return dayjs.utc(ms).format("YYYY-MM-DD HH:mm");
};

function SummaryRow({ label, children }) {
  return (
    <>
      <Typography sx={{ fontSize: "12px", color: PANEL_TEXT_FAINT }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "12px", color: WW_BLUE }}>
        {children}
      </Typography>
    </>
  );
}

function ModeRadioGroup({ value, onChange, ariaLabel }) {
  return (
    <RadioGroup
      row
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
      sx={{ mb: 0.25 }}
    >
      <FormControlLabel
        value="existing"
        control={<CustomRadio size="small" sx={{ py: 0.5 }} />}
        label={<Typography sx={RADIO_LABEL_SX}>Use existing</Typography>}
      />
      <FormControlLabel
        value="new"
        control={<CustomRadio size="small" sx={{ py: 0.5 }} />}
        label={<Typography sx={RADIO_LABEL_SX}>Create new</Typography>}
      />
    </RadioGroup>
  );
}

// Inline confirmation form rendered inside the Fleet Explorer panel while an
// import preview draft is on the map — deliberately not a modal dialog, so
// the previewed route stays visible.
export default function RouteImportForm({
  draftRoute,
  saving = false,
  onCancel,
  onConfirm,
}) {
  const { accessContext, setAccessContext } = useAccessContext();

  const [routeName, setRouteName] = useState("");
  const [vesselMode, setVesselMode] = useState("existing");
  const [voyageMode, setVoyageMode] = useState("existing");

  const vesselOptions = useVesselOptions();
  const [voyageOptions, setVoyageOptions] = useState([]);

  const [selectedVessel, setSelectedVessel] = useState(null);
  const [vesselTouched, setVesselTouched] = useState(false);
  const [selectedVoyage, setSelectedVoyage] = useState(null);
  const [voyageTouched, setVoyageTouched] = useState(false);
  const [newVesselName, setNewVesselName] = useState("");
  const [newVoyageName, setNewVoyageName] = useState("");
  const [departure, setDeparture] = useState(null);
  const [baseSpeed, setBaseSpeed] = useState("");
  const [baseSpeedInvalid, setBaseSpeedInvalid] = useState(false);

  useEffect(() => {
    if (!draftRoute) return;
    setDeparture(null);
    setBaseSpeed("");
    setBaseSpeedInvalid(false);
    setRouteName(draftRoute.routeName || "");
    setNewVesselName(draftRoute.vesselName || "");
    setNewVoyageName(draftRoute.routeName || "");
    setSelectedVessel(null);
    setVesselTouched(false);
    setSelectedVoyage(null);
    setVoyageTouched(false);
    setVesselMode("existing");
    setVoyageMode("existing");
  }, [draftRoute]);

  // Until the user picks; vessel options may arrive after the draft.
  useEffect(() => {
    if (!draftRoute || vesselTouched || selectedVessel) return;
    const vessel = preselectImportVessel(vesselOptions, draftRoute.vesselName, draftRoute.vesselImo);
    if (vessel) setSelectedVessel(vessel);
  }, [draftRoute, vesselOptions, vesselTouched, selectedVessel]);

  // The vessel's active voyage, once its options (not a previous vessel's) have loaded.
  useEffect(() => {
    if (voyageTouched || selectedVoyage || !selectedVessel) return;
    const active = voyageOptions.find(
      (v) => Number(v?.vesselId) === Number(selectedVessel.id) && v?.state === "active",
    );
    if (active) setSelectedVoyage(active);
  }, [voyageOptions, selectedVessel, selectedVoyage, voyageTouched]);

  useEffect(() => {
    if (vesselMode !== "existing") return;
    if (!selectedVessel || !selectedVessel.id) return;

    const fetchVoyages = async () => {
      try {
        const data = await coreGet(
          `/voyages?vessel_id=${selectedVessel.id}&type=planned&state=active`,
          accessContext,
          setAccessContext,
          ".core /voyages error=",
        );
        const sortedVoyages = [...(data?.voyages || [])].sort((a, b) => {
          const aTs = new Date(a?.timestamp ?? 0).getTime();
          const bTs = new Date(b?.timestamp ?? 0).getTime();
          return bTs - aTs;
        });
        setVoyageOptions(sortedVoyages);
      } catch (err) {
        console.error("Error fetching voyages:", err);
      }
    };
    fetchVoyages();
  }, [vesselMode, selectedVessel, accessContext, setAccessContext]);

  const sourceFormat = draftRoute?.sourceFormat ?? "voyageFile";
  const formatBlocked =
    vesselMode === "existing" &&
    Boolean(selectedVessel) &&
    !vesselAcceptsImportFormat(selectedVessel, sourceFormat);

  const departurePast = Boolean(departure?.isValid?.()) && departure.isBefore(nowUtc().startOf("minute"));
  const scheduleOk =
    !draftRoute?.needsSchedule ||
    (Boolean(departure?.isValid?.()) && !departurePast && baseSpeed !== "" && !baseSpeedInvalid);

  const canConfirm = useMemo(() => {
    const vesselOk =
      vesselMode === "existing"
        ? Boolean(selectedVessel && selectedVessel.id)
        : Boolean(newVesselName.trim());

    const voyageOk =
      voyageMode === "existing"
        ? vesselMode === "existing" && Boolean(selectedVoyage && selectedVoyage.id)
        : Boolean(newVoyageName.trim());

    return vesselOk && voyageOk && Boolean(routeName.trim()) && !formatBlocked && scheduleOk;
  }, [
    formatBlocked,
    scheduleOk,
    vesselMode,
    voyageMode,
    selectedVessel,
    selectedVoyage,
    newVesselName,
    newVoyageName,
    routeName,
  ]);

  const handleConfirm = () => {
    if (!draftRoute) return;
    onConfirm?.({
      draftRoute,
      routeName: routeName.trim(),
      vesselSelection:
        vesselMode === "existing"
          ? { mode: "existing", vessel: selectedVessel }
          : { mode: "new", name: newVesselName.trim() },
      voyageSelection:
        voyageMode === "existing"
          ? { mode: "existing", voyage: selectedVoyage }
          : { mode: "new", name: newVoyageName.trim() },
      schedule: draftRoute.needsSchedule
        ? { departureUtc: departure.toISOString(), basespeedKn: Number(baseSpeed) }
        : null,
    });
  };

  // Same boundaries as core's recalc window (strictly before now = past).
  const { pastHint, allPast } = useMemo(() => {
    const features = draftRoute?.geojson?.features ?? [];
    const cutoff = nowMs();
    const pastCount = features.filter((f) => {
      const ms = Date.parse(f?.properties?.timestamp ?? "");
      return Number.isFinite(ms) && ms < cutoff;
    }).length;
    if (pastCount === 0) return { pastHint: null, allPast: false };
    if (pastCount >= features.length) {
      return {
        pastHint: "All waypoints lie in the past: the route is imported as-is, without weather analysis.",
        allPast: true,
      };
    }
    return {
      pastHint: `${pastCount} of ${features.length} waypoints lie in the past: the route is analysed from the vessel's last reported position (or its estimated position on the plan), the past part is dropped.`,
      allPast: false,
    };
  }, [draftRoute]);

  if (!draftRoute) return null;

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          pr: 0.5,
          ...PANEL_SCROLLBAR_SX,
        }}
      >
        {!allPast && (
          <WarnChip
            severity="warning"
            sx={{ display: "flex", alignItems: "flex-start", fontWeight: 500, lineHeight: 1.4, mb: 1 }}
          >
            <span>
              <b>The route is analysed with the current weather on import.</b>{" "}
              Waypoint times (ETAs) are recalculated and replace the times from
              the file; waypoints and departure stay unchanged. This can take up
              to a minute.
            </span>
          </WarnChip>
        )}
        <Typography sx={{ ...ROW_DESCRIPTION_SX, mb: 1 }}>
          The map shows the route as in the file.
        </Typography>

        <Box sx={CARD_SX}>
          <Typography sx={{ ...CARD_TITLE_SX, mb: 1 }}>
            Imported Route
          </Typography>
          <Typography sx={{ ...ROW_LABEL_SX, mb: 0.5 }}>Route name</Typography>
          <TextField
            size="small"
            fullWidth
            value={routeName}
            onChange={(e) => setRouteName(e.target.value)}
            sx={{ ...PLAN_FIELD_SX, mb: 1 }}
          />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr",
              rowGap: 0.5,
              columnGap: 1,
            }}
          >
            <SummaryRow label="Format">
              {routeFormatLabel(sourceFormat)}
            </SummaryRow>
            <SummaryRow label="Waypoints">
              {draftRoute.waypointCount ?? 0}
            </SummaryRow>
            <SummaryRow label="Start / End (UTC)">
              {formatTimeUtc(draftRoute.startTime)} →{" "}
              {formatTimeUtc(draftRoute.endTime)}
            </SummaryRow>
          </Box>
          {pastHint && (
            <Typography sx={{ ...ROW_DESCRIPTION_SX, mt: 1, color: "#b7791f" }}>
              {pastHint}
            </Typography>
          )}
        </Box>

        {draftRoute.needsSchedule && (
          <Box sx={CARD_SX}>
            <Typography sx={{ ...CARD_TITLE_SX, mb: 0.5 }}>Departure & Speed</Typography>
            <Typography sx={{ ...ROW_DESCRIPTION_SX, mb: 1 }}>
              The file has no times. Waypoint times are calculated from the
              departure (UTC) and the base speed.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <Box sx={{ flex: 1.4, minWidth: 0 }}>
                <Typography noWrap sx={PLAN_FIELD_CAPTION_SX}>
                  Time (UTC)
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    label={null}
                    value={departure}
                    onChange={(value) => setDeparture(value ? dayjs(value).utc() : null)}
                    timezone="UTC"
                    minDateTime={nowUtc().startOf("minute")}
                    ampm={false}
                    format="YYYY-MM-DD HH:mm"
                    slotProps={{
                      ...PLAN_DATEPICKER_SLOTPROPS,
                      textField: {
                        fullWidth: true,
                        variant: "outlined",
                        error: departurePast,
                        helperText: departurePast ? "Departure time is in the past." : "",
                        placeholder: "Select departure time",
                        sx: PLAN_DATEPICKER_FIELD_SX,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
              <Box sx={{ flex: 0.8, minWidth: 0 }}>
                <Typography noWrap sx={PLAN_FIELD_CAPTION_SX}>
                  Base Speed
                </Typography>
                <TextField
                  type="text"
                  fullWidth
                  inputMode="decimal"
                  value={baseSpeed}
                  onChange={(e) => {
                    const { value, invalid } = validateDecimal(e.target.value, BASESPEED_RULES);
                    setBaseSpeed(value);
                    setBaseSpeedInvalid(invalid);
                  }}
                  error={baseSpeedInvalid}
                  helperText={baseSpeedInvalid ? "Between 0 and 40 kn" : ""}
                  slotProps={{
                    formHelperText: { sx: { m: 0, p: 0 } },
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography sx={PLAN_UNIT_ADORNMENT_SX}>kn</Typography>
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={PLAN_SPEED_FIELD_SX}
                />
              </Box>
            </Box>
          </Box>
        )}

        <Box sx={CARD_SX}>
          <Typography sx={{ ...CARD_TITLE_SX, mb: 0.5 }}>Vessel</Typography>
          <ModeRadioGroup
            value={vesselMode}
            onChange={setVesselMode}
            ariaLabel="import vessel mode"
          />
          {vesselMode === "existing" ? (
            <VesselAutocomplete
              options={vesselOptions}
              value={selectedVessel}
              onChange={(_e, newVal) => {
                setVesselTouched(true);
                setSelectedVessel(newVal || null);
                setSelectedVoyage(null);
                setVoyageTouched(false);
              }}
              placeholder="Select vessel..."
            />
          ) : (
            <TextField
              size="small"
              fullWidth
              placeholder="New vessel name"
              value={newVesselName}
              onChange={(e) => setNewVesselName(e.target.value)}
              sx={PLAN_FIELD_SX}
            />
          )}
          {formatBlocked && (
            <Typography sx={{ ...ROW_DESCRIPTION_SX, mt: 0.75, color: "error.main" }}>
              {routeFormatLabel(sourceFormat)} is not
              an import format of this vessel.
            </Typography>
          )}
        </Box>

        <Box sx={CARD_SX}>
          <Typography sx={{ ...CARD_TITLE_SX, mb: 0.5 }}>Voyage</Typography>
          <ModeRadioGroup
            value={voyageMode}
            onChange={setVoyageMode}
            ariaLabel="import voyage mode"
          />
          {voyageMode === "existing" ? (
            <Autocomplete
              size="small"
              options={voyageOptions}
              value={selectedVoyage}
              onChange={(_e, newVal) => {
                setVoyageTouched(true);
                setSelectedVoyage(newVal || null);
              }}
              getOptionLabel={(opt) => opt?.name || ""}
              isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
              disabled={vesselMode === "new" || !selectedVessel}
              popupIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
              slotProps={AUTOCOMPLETE_LISTBOX_SLOTPROPS}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select voyage..."
                  sx={{
                    ...PLAN_FIELD_SX,
                    "& .MuiAutocomplete-popupIndicator": {
                      color: PANEL_TEXT_FAINT,
                    },
                  }}
                  slotProps={{
                    ...params.slotProps,
                    input: {
                      ...params.slotProps?.input,
                      startAdornment: (
                        <>
                          <InputAdornment
                            position="start"
                            sx={{ ml: 0.25, mr: 0 }}
                          >
                            <SwipeRightAltIcon
                              sx={{ fontSize: 17, color: alpha(WW_BLUE, 0.7) }}
                            />
                          </InputAdornment>
                          {params.slotProps?.input?.startAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
          ) : (
            <TextField
              size="small"
              fullWidth
              placeholder="New voyage name"
              value={newVoyageName}
              onChange={(e) => setNewVoyageName(e.target.value)}
              sx={PLAN_FIELD_SX}
            />
          )}
        </Box>
      </Box>

      <WizardFooter
        secondaryLabel="Cancel"
        onSecondary={onCancel}
        primaryLabel={saving ? "Importing …" : "Confirm Import"}
        onPrimary={handleConfirm}
        primaryDisabled={!canConfirm || saving}
        primaryLoading={saving}
      />
    </Box>
  );
}
